import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenInput"]

  connect() {
    this.healthInsurances = []
    this.loadHealthInsurances()
    this.setupEventListeners()
    this.updateFieldStatus()
  }

  disconnect() {
    this.removeEventListeners()
  }

  async loadHealthInsurances() {
    try {
      const response = await fetch('/health_insurances.json', {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      this.healthInsurances = await response.json()
    } catch (error) {
      console.error('Error loading health insurances:', error)
    }
  }

  setupEventListeners() {
    this.inputTarget.addEventListener('input', this.handleInput.bind(this))
    this.inputTarget.addEventListener('keydown', this.handleKeydown.bind(this))
    this.inputTarget.addEventListener('focus', this.handleFocus.bind(this))
    this.inputTarget.addEventListener('blur', this.hideDropdown.bind(this))
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  removeEventListeners() {
    this.inputTarget.removeEventListener('input', this.handleInput.bind(this))
    this.inputTarget.removeEventListener('keydown', this.handleKeydown.bind(this))
    this.inputTarget.removeEventListener('focus', this.handleFocus.bind(this))
    this.inputTarget.removeEventListener('blur', this.hideDropdown.bind(this))
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
  }

  handleInput(event) {
    const originalQuery = event.target.value
    const query = originalQuery.toLowerCase()
    this.filterOptions(query, originalQuery)
    this.updateFieldStatus()
  }

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const query = this.inputTarget.value.trim()
      if (query) {
        this.createHealthInsurance(query)
      }
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  handleFocus(event) {
    this.showAllOptions()
  }

  filterOptions(query, originalQuery = '') {
    
    if (!query) {
      this.showAllOptions()
      return
    }

    const filtered = this.healthInsurances.filter(insurance => 
      insurance.name.toLowerCase().includes(query)
    )

    if (filtered.length === 0) {
      this.showCreateOption(originalQuery || query)
    } else {
      this.showFilteredOptions(filtered, originalQuery || query)
    }
  }

  showAllOptions() {
    this.dropdownTarget.innerHTML = this.healthInsurances
      .map(insurance => this.createOptionHTML(insurance.name, insurance.id, false))
      .join('')
    this.showDropdown()
  }

  showFilteredOptions(filtered, query) {
    // Check if there's already a health insurance with the same name (case-insensitive)
    const existingInsurance = this.healthInsurances.find(insurance => 
      insurance.name.toLowerCase() === query.toLowerCase()
    )
    
    if (existingInsurance) {
      // Don't show create option if it already exists
      this.dropdownTarget.innerHTML = filtered
        .map(insurance => this.createOptionHTML(insurance.name, insurance.id, false))
        .join('')
    } else {
      // Show create option if it doesn't exist
      this.dropdownTarget.innerHTML = filtered
        .map(insurance => this.createOptionHTML(insurance.name, insurance.id, false))
        .join('') + this.createOptionHTML(`Crear "${query}"`, null, true)
    }
    this.showDropdown()
  }

  showCreateOption(query) {
    // Check if there's already a health insurance with the same name (case-insensitive)
    const existingInsurance = this.healthInsurances.find(insurance => 
      insurance.name.toLowerCase() === query.toLowerCase()
    )
    
    if (existingInsurance) {
      // If it exists, show it as a regular option instead of create option
      this.dropdownTarget.innerHTML = this.createOptionHTML(existingInsurance.name, existingInsurance.id, false)
    } else {
      // Show create option if it doesn't exist
      this.dropdownTarget.innerHTML = this.createOptionHTML(`Crear "${query}"`, null, true)
    }
    this.showDropdown()
  }

  createOptionHTML(text, value, isCreate = false) {
    const className = isCreate ? 'dropdown-item create-option' : 'dropdown-item'
    const dataValue = value ? `data-value="${value}"` : ''
    return `<div class="${className}" ${dataValue}>${text}</div>`
  }

  showDropdown() {
    this.dropdownTarget.style.display = 'block'
    this.setupDropdownClickListeners()
  }

  hideDropdown() {
    setTimeout(() => {
      this.dropdownTarget.style.display = 'none'
    }, 150)
  }

  setupDropdownClickListeners() {
    const options = this.dropdownTarget.querySelectorAll('.dropdown-item')
    options.forEach(option => {
      option.addEventListener('click', (event) => {
        event.preventDefault()
        const text = option.textContent
        const value = option.dataset.value
        
        if (option.classList.contains('create-option')) {
          // Extract the name from "Crear 'nombre'"
          const name = text.match(/Crear "(.+)"/)?.[1] || text
          this.createHealthInsurance(name)
        } else {
          this.selectOption(text, value)
        }
      })
    })
  }

  selectOption(text, value) {
    this.inputTarget.value = text
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = value
    }
  }

  updateFieldStatus() {
    const hasValue = this.inputTarget.value.trim() !== '' && this.hiddenInputTarget.value !== ''
    
    if (hasValue) {
      this.inputTarget.classList.add('is-valid')
      this.inputTarget.classList.remove('is-invalid')
      this.inputTarget.nextElementSibling.style.right = '30px'
    } else {
      this.inputTarget.classList.remove('is-valid')
      this.inputTarget.classList.remove('is-invalid')
      this.inputTarget.nextElementSibling.style.right = '10px'
    }
  }

  async createHealthInsurance(name) {
    // Check if a health insurance with the same name (case-insensitive) already exists
    const existingInsurance = this.healthInsurances.find(insurance => 
      insurance.name.toLowerCase() === name.toLowerCase()
    )
    
    if (existingInsurance) {
      this.showNotification(`La prepaga "${existingInsurance.name}" ya existe`, 'error')
      // Select the existing option instead
      this.selectOption(existingInsurance.name, existingInsurance.id)
      return
    }
    
    try {
      const response = await fetch('/health_insurances', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          health_insurance: { name: name }
        })
      })

      const data = await response.json()
      
      if (data.success) {
        // Add to local list
        this.healthInsurances.push(data.health_insurance)
        
        // Select the new option
        this.selectOption(data.health_insurance.name, data.health_insurance.id)
        
        // Show success message
        this.showNotification('Prepaga creada exitosamente', 'success')
      } else {
        this.showNotification('Error al crear la prepaga: ' + data.errors.join(', '), 'error')
      }
    } catch (error) {
      console.error('Error creating health insurance:', error)
      this.showNotification('Error al crear la prepaga', 'error')
    }
  }

  showNotification(message, type) {
    const notification = document.createElement('div')
    
    // Map type to flash key
    const flashKey = type === 'success' ? 'success' : 'danger'
    
    notification.className = `flash flash-${flashKey} alert alert-dismissible fade show`
    notification.setAttribute('role', 'alert')
    notification.setAttribute('data-flash-key', flashKey)
    notification.setAttribute('data-controller', 'flash')
    notification.setAttribute('data-flash-auto-dismiss-value', 'true')
    notification.setAttribute('data-flash-dismiss-delay-value', '2000')
    notification.style.cssText = 'z-index: 9999;'
    
    notification.innerHTML = `
      <span class="w-100 text-center">${message}</span>
      <a data-dismiss="alert" aria-label="Close" class="text-secondary">
        <i class="fas fa-times"></i>
      </a>
    `
    
    document.body.appendChild(notification)
    
    // The flash controller will handle auto-dismiss, so we don't need the manual timeout here
  }
}
